import React, { useEffect, useRef, useState } from 'react';
import { View, Keyboard, ScrollView, Text, Image, TouchableOpacity, ActivityIndicator } from 'react-native-web';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import { useRoute } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/dist/Feather';
import * as Animatable from 'react-native-animatable';

// Custom Components
import { Button, NavBar, ReviewItem, TextBox, TierBlockView } from '../../components';
import LoadingView from '../../components/component_loading_view';

// Assets
import * as lottieFile from '../../lotties/lottie_review_reply.json';

// Utils
import { isProdTestAccount } from '../../utils/util_truck_helpers';
import { showAlert, showAppError } from '../../utils/util_helpers';

// APIs
import { deleteReviewReply, editReviewReply, markReviewAsRead, sendReviewReply } from '../../modules';

// Configs
import GLOBAL from '../../configs/config_global';
import { getGlobalState, STATE_KEYS } from '../../configs/config_state';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';

export function ReviewDetails() {
    const { reviewData: dataFromRoute, onReviewUpdated, onReplyRead } = useRoute()?.params || {};

    // region Refs
    const hasAccess = isProdTestAccount() || (GLOBAL.DATA.USER['tier'] > 0);
    const contentScrollView = useRef(null);
    const replyInput = useRef(null);
    // endregion

    // region Local State
    const [reviewData, setReviewData] = useState(dataFromRoute);
    const [editingReplyText, setEditingReplyText] = useState('');
    const [isSendingReply, setSendingReply] = useState(false);
    const [isDeletingReply, setDeletingReply] = useState(false);
    const [isUpdatingReply, setUpdatingReply] = useState(false);
    const [selectedReplyItem, setSelectedReplyItem] = useState(null);
    // endregion

    // region Component Mounting
    useEffect(() => {
        markReviewAsRead(reviewData.id)
            .then((result) => {
                // Callback to Profile tab
                onReplyRead(result['unread_count']);
            })
    }, []);
    // endregion

    // region Function - Send Reply
    const composeNewReply = () => {
        showAlert({
            text: 'Enter your reply',
            showTextInput: true,
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Send Reply',
                color: GLOBAL.COLOR.MAIN,
                onPress: (text) => goSendReply(text)
            }
        })
    };

    const goSendReply = (text) => {
        Keyboard.dismiss();

        if (!text?.trim()) return;

        setSendingReply(true);
        setTimeout(() => {
            sendReviewReply(reviewData['id'], text)
                .then((newReply) => {
                    // Create a new array that contain the current replies and the new one
                    const newReviewData = cloneDeep(reviewData);
                    newReviewData['reply'] = reviewData['reply'].concat([newReply]);

                    // Load new data to state
                    setReviewData(newReviewData);
                    setTimeout(() => contentScrollView.current?.scrollToEnd(), 500);

                    // Push data for parent scene
                    (onReviewUpdated) && onReviewUpdated(newReviewData);

                    // Notify user
                    toast('Reply submitted');
                })
                .catch((error) => showAppError(error))
                .finally(() => setSendingReply(false));
        }, 1000);
    };
    // endregion

    // region Function - Edit Reply
    const editReply = (replyItem) => {
        setSelectedReplyItem(replyItem);
        setEditingReplyText(replyItem.comment);
        setTimeout(() => replyInput.current?.focus(), 250);
    }

    const goEditReply = () => {
        Keyboard.dismiss();

        const replyID = selectedReplyItem?.id;

        setUpdatingReply(true);
        setTimeout(() => {
            editReviewReply(reviewData.id, replyID, editingReplyText)
                .then(() => {
                    const tempReviewData = cloneDeep(reviewData);
                    const tempReplyData = Object.assign(cloneDeep(selectedReplyItem), { comment: editingReplyText, updated_at: moment().utc().format('YYYY-MM-DD HH:mm:ss') });
                    const replyIndex = tempReviewData['reply'].findIndex((reply) => reply.id === replyID);

                    (replyIndex > -1) && tempReviewData['reply'].splice(replyIndex, 1, tempReplyData);

                    setSelectedReplyItem(null);
                    setEditingReplyText('');
                    setReviewData(tempReviewData);

                    // Notify
                    toast('Reply updated successfully');

                    // Callback
                    (onReviewUpdated) && onReviewUpdated(tempReviewData);
                })
                .catch((error) => showAppError(error))
                .finally(() => setUpdatingReply(false));
        }, 1000);
    }
    // endregion

    // region Function - Remove Reply
    const goRemoveReply = (replyData) => {
        showAlert({
            text: `Are you sure you want to remove this reply?<br/><br/>"${replyData.comment}"`,
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: 'red',
                onPress: () => {
                    setDeletingReply(true);
                    setTimeout(() => {
                        deleteReviewReply(reviewData.id, replyData.id)
                            .then(() => {
                                // Generate new review data object
                                const newReviewData = cloneDeep(reviewData);
                                newReviewData.reply = reviewData['reply'].filter((item) => item.id !== replyData.id);

                                // Set data to state
                                setReviewData(newReviewData);

                                // Pass data back to parent scene
                                (onReviewUpdated) && onReviewUpdated(newReviewData);

                                // Notify user
                                toast('Reply removed successfully');
                            })
                            .catch((error) => showAppError(error))
                            .finally(() => setDeletingReply(false));
                    }, 1000);
                }
            }
        })
    }
    // endregion

    // region Render Reply Item
    const renderReplyItem = (item, index) => {
        const isLastItem = index === (reviewData['reply']?.length - 1);
        const truckData = getGlobalState(STATE_KEYS.TRUCK_DATA);
        const isMyReply = item['sender_type'] === 0;
        const anotherReplySelected = !!selectedReplyItem && (selectedReplyItem.id !== item.id);
        const thisReplySelected = !!selectedReplyItem && (selectedReplyItem.id === item.id);
        return(
            <View
                key={`replyItem_${item.id}`}
                pointerEvents={anotherReplySelected ? 'none' : 'auto'}
                style={{ width: '100%', backgroundColor: 'transparent', flexDirection: 'row', paddingHorizontal: 24, opacity: anotherReplySelected ? 0.3 : 1 }}>
                {/* Line */}
                <View style={{ width: 64, height: '100%', opacity: (thisReplySelected || anotherReplySelected) ? 0.3 : 1 }}>
                    <View style={{ width: 4, height: isLastItem ? 64 : '100%', backgroundColor: GLOBAL.COLOR.BASIC.COVER_GRAY, alignSelf: 'center' }} />
                    <View style={{
                        position: 'absolute', right: 0, top: 60,
                        height: 4, width: 32, backgroundColor: GLOBAL.COLOR.BASIC.COVER_GRAY
                    }} />
                </View>

                {/* Main Reply Content */}
                <View style={{ flex: 1, marginTop: 24, backgroundColor: 'white', borderRadius: 8, padding: 16, flexDirection: 'row', alignItems: 'center', overflow: 'hidden' }}>
                    {/* Content */}
                    <View style={{ flex: 1 }}>
                        {/* User info and rating */}
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            {/* User Avatar */}
                            <View style={{ width: 40, height: 40, borderRadius: 20, overflow: 'hidden' }}>
                                {
                                    isMyReply ?
                                        <Image
                                            source={truckData['self_logo'] ? { uri: truckData['self_logo'] } : (truckData.logo ? { uri: truckData.logo[0].url } : (truckData.cover_photo ? { uri: truckData.cover_photo[0].url } : require('../../images/logo_truckster_white.png')))}
                                            style={{ width: 50, height: 50 }}
                                            resizeMode={truckData['self_logo'] ? 'contain' : (truckData.cover_photo ? 'cover' : 'stretch')}
                                        />
                                        :
                                        <Image
                                            source={reviewData.user?.avatar ? { uri: reviewData.user?.avatar } : require('../../images/image_user_placeholder_round.png')}
                                            style={{ width: 40, height: 40, backgroundColor: GLOBAL.COLOR.TEXT_LIGHT_GRAY }}
                                            resizeMode={'cover'}
                                        />
                                }
                            </View>

                            {/* Name and submit date */}
                            <View style={{ flex: 1, marginLeft: 16 }}>
                                {/* User Name */}
                                <Text style={{ color: isMyReply ? GLOBAL.COLOR.BASIC.UI_BLUE : GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: '500', fontSize: 16 }}>
                                    {isMyReply ? GLOBAL.DATA.USER.name + ' (You)' : (reviewData.user?.name || '(Deleted User)')}
                                </Text>

                                {/* Submitted Date */}
                                <Text style={{ color: GLOBAL.COLOR.BASIC.PINKISH_GREY, marginTop: 4, fontSize: 15 }}>
                                    {moment.utc(item['created_at']).fromNow()}{(item['updated_at'] && (item['created_at'] !== item['updated_at'])) ? ' (edited)' : ''}
                                </Text>
                            </View>
                        </View>

                        {/* Comment Content */}
                        {
                            thisReplySelected ?
                                <TextBox
                                    ref={replyInput}
                                    style={{ marginTop: 24 }}
                                    value={editingReplyText}
                                    onChangeText={setEditingReplyText}
                                    title={'Editing Reply'}
                                    placeholder={'Editing Reply'}
                                />
                                :
                                <Text style={{ fontSize: 18, lineHeight: 28, color: GLOBAL.COLOR.BASIC.WARM_GREY, alignSelf: 'stretch', marginTop: 24 }}>
                                    {item.comment.trim()}
                                </Text>
                        }
                    </View>

                    {/* Edit Button */}
                    {
                        (isMyReply && !thisReplySelected) &&
                        <TouchableOpacity
                            onPress={() => editReply(item)}
                            style={{ width: 64, height: 64, alignItems: 'center', justifyContent: 'center' }}>
                            <FeatherIcon name={'edit'} color={GLOBAL.COLOR.TEXT_GRAY} size={24} />
                        </TouchableOpacity>
                    }

                    {/* Delete Button */}
                    {
                        (isMyReply && !thisReplySelected) &&
                        <TouchableOpacity
                            onPress={() => {
                                if (isLastItem) {
                                    goRemoveReply(item);
                                }
                                else showAlert({ text: 'There is a newer reply already, you cannot delete this reply anymore' });
                            }}
                            style={{ width: 64, height: 64, alignItems: 'center', justifyContent: 'center', opacity: isLastItem ? 1 : 0.3 }}>
                            <FeatherIcon name={'trash'} color={GLOBAL.COLOR.BASIC.RED} size={24} />
                        </TouchableOpacity>
                    }

                    {/* Confirm / Cancel Edit Buttons */}
                    {
                        thisReplySelected &&
                        <View style={{ position: 'absolute', top: 16, right: 16, flexDirection: 'row', alignItems: 'center' }}>
                            {/* Confirm */}
                            <Button
                                onPress={() => goEditReply()}
                                text={'Confirm'}
                                textStyle={{ color: 'white', fontSize: 14, marginLeft: 16, marginRight: 16 }}
                                style={{ height: 40, marginRight: 8 }}
                                locked={editingReplyText?.trim()?.toLowerCase()?.localeCompare(selectedReplyItem?.comment?.trim()?.toLowerCase()) === 0}
                            />

                            {/* Cancel */}
                            <Button
                                onPress={() => {
                                    setEditingReplyText('');
                                    setSelectedReplyItem(null);
                                }}
                                text={'Cancel'}
                                textStyle={{ color: 'white', fontSize: 14, marginLeft: 16, marginRight: 16 }}
                                style={{ height: 40, backgroundColor: GLOBAL.COLOR.BASIC.GRAY }}
                            />
                        </View>
                    }

                    {/* Edit Loading */}
                    {
                        (thisReplySelected && isUpdatingReply) &&
                        <Animatable.View
                            animation={'fadeIn'} duration={600}
                            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
                            <ActivityIndicator color={'white'} size={24} />

                            {/* Text */}
                            <Text style={{ color: 'white', fontSize: 16, fontWeight: 'bold', marginLeft: 16 }}>
                                Updating your reply
                            </Text>
                        </Animatable.View>
                    }
                </View>
            </View>
        );
    }
    // endregion

    // region Render Reply Input
    const renderReplyInput = () => {
        return(<View />);
    }
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ width: '100%', flex: 1 }}>
            {/* Nav Bar */}
            <NavBar
                leftButtonOnPress={() => RootNavigation.goBack()}
                title={'Review Detail'}
                leftButtonIconName={'arrow-back'}
            />

            {/* Main Content */}
            <View style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
                {/* Review Content */}
                <View style={{ alignSelf: 'stretch', marginHorizontal: 24, marginTop: 24, backgroundColor: 'white', borderWidth: 1, borderRadius: 6, borderColor: GLOBAL.COLOR.DIVIDER }}>
                    <ReviewItem
                        reviewData={reviewData}
                        onReplyToVendorPressed={null}
                        hideVendorResponse={true}
                        showTotalResponses={false}
                        parentPadding={24}
                        showUnreadBadge={false}
                    />
                </View>

                {/* Reply List */}
                {
                    hasAccess ?
                        <ScrollView
                            style={{ width: '100%', flex: 1, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                            contentContainerStyle={{ paddingBottom: 32 }}>
                            {/* Reply Items */}
                            {(reviewData['reply'] || []).map((replyItem, index) => renderReplyItem(replyItem, index))}

                            {/* Send Reply Button */}
                            <Button
                                onPress={() => composeNewReply()}
                                text={'Send new reply'}
                                textStyle={{ color: 'white', fontSize: 16 }}
                                style={{ marginLeft: 88, marginRight: 24, marginTop: 24 }}
                                image={'reply'}
                                imageSize={20}
                                imageNextToText={true}
                                loadingText={'Sending your reply'}
                                isLoading={isSendingReply}
                            />
                        </ScrollView>
                        :
                        <View style={{ width: '100%', paddingTop: 128 }}>
                            <TierBlockView
                                style={{ backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                                lottieFile={lottieFile}
                                sectionBenefit={'Reply to your customer reviews to let them know that you care about them!'}
                                availableTier={1}
                            />
                        </View>
                }
            </View>


            {/* Reply Input */}
            {hasAccess && renderReplyInput()}

            {/* Loading View */}
            {isDeletingReply && <LoadingView isAbsolute={true}/>}
        </View>
    );
    // endregion
}
